import { EddConfig, ExcludedDate } from '@/data/config/eddConfig';
import moment from 'moment';
import 'moment/locale/es.js';

const MAX_ITERATIONS = 30;
const FALLBACK_TIMEZONE = 'America/Mexico_City';

export const resolveTimeZone = (): string => {
	try {
		return Intl.DateTimeFormat().resolvedOptions().timeZone || FALLBACK_TIMEZONE;
	} catch {
		return FALLBACK_TIMEZONE;
	}
};

/**
 * Exported so other EDD-adjacent calculations (e.g. the PDP pickup-ready-time in
 * `eddPickupReadyTime.ts`) evaluate "now" in the same browser/store-timezone
 * resolution as the cutoff check, rather than re-deriving it (AC25/AC26/AC27 of
 * story 158621 govern this without change).
 */
export const getHoursAndMinutes = (date: Date): { hours: number; minutes: number } => {
	const parts = new Intl.DateTimeFormat('en-US', {
		timeZone: resolveTimeZone(),
		hour12: false,
		hour: '2-digit',
		minute: '2-digit',
	}).formatToParts(date);
	const hours = Number.parseInt(parts.find((part) => part.type === 'hour')?.value ?? '0', 10);
	const minutes = Number.parseInt(parts.find((part) => part.type === 'minute')?.value ?? '0', 10);
	// Intl formats midnight as "24" under hour12: false in some environments.
	return { hours: hours === 24 ? 0 : hours, minutes };
};

/** AC10/AC13/AC26/AC27: cutoff is evaluated against the customer's browser time (store timezone as fallback). */
export const isPastCutoff = (cutoffTime: string, now: Date = new Date()): boolean => {
	const [cutoffHours, cutoffMinutes] = cutoffTime
		.split(':')
		.map((part) => Number.parseInt(part, 10));
	const { hours, minutes } = getHoursAndMinutes(now);
	return hours > cutoffHours || (hours === cutoffHours && minutes >= cutoffMinutes);
};

export const isExcludedDay = (
	date: Date,
	excludedWeekdays: number[],
	excludedDates: ExcludedDate[]
): boolean =>
	excludedWeekdays.includes(date.getDay()) ||
	excludedDates.some(
		(excluded) => excluded.month === date.getMonth() + 1 && excluded.day === date.getDate()
	);

export type EddChannel = 'delivery' | 'pickup';

export type EddDateResult = {
	descriptor: 'today' | 'tomorrow' | 'date';
	date: Date;
	/** dd mmm, e.g. "22 ago" (AC22). */
	formatted: string;
};

const addDays = (date: Date, days: number): Date =>
	new Date(date.getFullYear(), date.getMonth(), date.getDate() + days);

/**
 * Shared date-calculation utility (AC16-AC22): adds `daysToAdd` calendar days to
 * today, then shifts forward off an excluded landing day.
 *
 * Excluded weekdays and holidays are NOT skipped while counting — they consume
 * leadtime like any other day. They only matter for the resulting date: if that
 * lands on an excluded weekday or holiday it moves to the next day that is neither
 * (AC19). This supersedes AC18 as originally written, which had excluded days not
 * consuming leadtime; Product corrected the rule.
 *
 * Because the only traversal is the trailing shift, the AC20 cap of 30 iterations
 * applies literally — a large leadtime (e.g. a 128-day Special Order) costs no
 * iterations, while a misconfiguration where every day is excluded still fails
 * deterministically.
 */
export const calculateEddDate = (
	{
		daysToAdd,
		channel,
		skipExclusions,
	}: {
		daysToAdd: number;
		channel: EddChannel;
		skipExclusions?: boolean;
	},
	config: EddConfig,
	now: Date = new Date()
): EddDateResult | null => {
	const excludedWeekdays =
		channel === 'delivery' ? config.deliveryExcludedWeekdays : config.pickupExcludedWeekdays;
	const excludedDates =
		channel === 'delivery' ? config.deliveryExcludedDates : config.pickupExcludedDates;

	const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());

	// Plain calendar addition: an excluded day in between still consumes leadtime.
	let cursor = addDays(startOfToday, Math.max(daysToAdd, 0));
	// Dropship/Special Order (159992): the SKU's own transit time already accounts
	// for excluded days, so the trailing shift below must not run for them.
	let succeeded = !!skipExclusions;
	// eslint-disable-next-line functional/no-loop-statement
	for (let iteration = 0; !succeeded && iteration <= MAX_ITERATIONS; iteration += 1) {
		if (!isExcludedDay(cursor, excludedWeekdays, excludedDates)) {
			succeeded = true;
			break;
		}
		cursor = addDays(cursor, 1);
	}

	if (!succeeded) {
		// eslint-disable-next-line no-console
		console.warn(
			`[EDD] date calculation exceeded ${MAX_ITERATIONS} iterations for channel "${channel}" — check the ` +
				`EDD_${channel.toUpperCase()}_EXCLUDED_WEEKDAYS/EDD_${channel.toUpperCase()}_EXCLUDED_DATES storeconf for misconfiguration`
		);
		return null;
	}

	const diffDays = Math.round((cursor.getTime() - startOfToday.getTime()) / (1000 * 60 * 60 * 24));
	const descriptor: EddDateResult['descriptor'] =
		diffDays === 0 ? 'today' : diffDays === 1 ? 'tomorrow' : 'date';

	moment.locale('es');
	const month = moment(cursor).format('MMM').replace('.', '');
	const formatted = `${moment(cursor).format('DD')} ${month.charAt(0).toUpperCase()}${month.slice(
		1
	)}`;

	return { descriptor, date: cursor, formatted };
};
